import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';

import { map, type Observable, shareReplay } from 'rxjs';

import { ThemeService } from '@core/services/theme.service';

interface GradientConfig {
  start?: string;
  middle?: string;
  stop?: string;
}

interface PresetWithBranding {
  branding?: {
    gradients?: {
      primary?: GradientConfig;
      secondary?: GradientConfig;
      tertiary?: GradientConfig;
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class SvgIconService {
  private readonly httpClient = inject(HttpClient);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly themeService = inject(ThemeService);

  private readonly cache = new Map<string, Observable<string>>();
  private readonly maxCacheSize = 100;

  getIcon(name: string, size: string, color: string, gradient?: string): Observable<SafeHtml> {
    if (!this.cache.has(name)) {
      this.cache.set(
        name,
        this.httpClient.get(`assets/svg-icons/${name}.svg`, { responseType: 'text' }).pipe(shareReplay(1)),
      );
      if (this.cache.size >= this.maxCacheSize) {
        const oldestKey = this.cache.keys().next().value;
        if (oldestKey) {
          this.cache.delete(oldestKey);
        }
      }
    }
    return this.cache.get(name)!.pipe(map((svg) => this.processSvg(svg, name, size, color, gradient)));
  }

  private processSvg(svgContent: string, name: string, size: string, color: string, gradient?: string): SafeHtml {
    // Modify SVG string to set width/height
    // First remove any existing width/height attributes, then add new ones
    const sizeValue = size.startsWith('var(') ? '100%' : size;
    let updatedSvg = svgContent.replace(/<svg([^>]*)>/, (_, attrs: string) => {
      const cleanedAttrs = attrs.replace(/\s*(width|height)="[^"]*"/g, '');
      return `<svg${cleanedAttrs} width="${sizeValue}" height="${sizeValue}">`;
    });

    // Check if SVG contains linearGradient - if so, apply config colors
    const hasLinearGradient = updatedSvg.toLowerCase().includes('<lineargradient');

    if (gradient) {
      updatedSvg = this.injectGradient(updatedSvg, name, gradient);
    } else if (hasLinearGradient) {
      updatedSvg = this.applyGradientColors(updatedSvg);
    } else {
      updatedSvg = this.applyPathColors(updatedSvg, color);
    }

    return this.domSanitizer.bypassSecurityTrustHtml(updatedSvg);
  }

  private injectGradient(svg: string, iconName: string, gradientName: string): string {
    const preset = this.themeService.themePreset() as PresetWithBranding;
    const gradientConfig = preset.branding?.gradients?.[gradientName as keyof typeof preset.branding.gradients];

    if (!gradientConfig) {
      return svg;
    }

    const gradientId = `gradient-${iconName}`;
    const gradientDef = `
      <defs>
        <linearGradient id="${gradientId}" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stop-color="${gradientConfig.start}" />
          <stop offset="50%" stop-color="${gradientConfig.middle}" />
          <stop offset="100%" stop-color="${gradientConfig.stop}" />
        </linearGradient>
      </defs>`;

    // Inject gradient definition after opening svg tag
    let updatedSvg = svg.replace(/<svg([^>]*)>/, `<svg$1>${gradientDef}`);

    // Apply gradient to paths
    updatedSvg = updatedSvg.replace(/<path([^>]*)>/g, (_, attributes: string) => {
      let modifiedAttributes = attributes;

      if (attributes.includes('stroke=')) {
        modifiedAttributes = modifiedAttributes.replace(/stroke="[^"]*"/, `stroke="url(#${gradientId})"`);
      }

      if (attributes.includes('fill=') && !attributes.includes('fill="none"')) {
        modifiedAttributes = modifiedAttributes.replace(/fill="[^"]*"/, `fill="url(#${gradientId})"`);
      }

      return `<path${modifiedAttributes}>`;
    });

    return updatedSvg;
  }

  private applyGradientColors(svg: string): string {
    // Replace gradient stop colors with config values
    const preset = this.themeService.themePreset() as PresetWithBranding;
    const gradientPrimary = preset.branding?.gradients?.primary;

    if (!gradientPrimary) {
      return svg;
    }

    let updatedSvg = svg;

    // First stop (start) - no offset or offset="0"
    if (gradientPrimary.start) {
      updatedSvg = updatedSvg.replace(
        /<stop\s+(?:offset="0"\s+)?stop-color="[^"]*"/,
        `<stop stop-color="${gradientPrimary.start}"`,
      );
    }

    // Middle stop - offset="0.16" or similar
    if (gradientPrimary.middle) {
      updatedSvg = updatedSvg.replace(
        /<stop\s+offset="0\.16"\s+stop-color="[^"]*"/,
        `<stop offset="0.16" stop-color="${gradientPrimary.middle}"`,
      );
    }

    // Last stop - offset="1"
    if (gradientPrimary.stop) {
      updatedSvg = updatedSvg.replace(
        /<stop\s+offset="1"\s+stop-color="[^"]*"/,
        `<stop offset="1" stop-color="${gradientPrimary.stop}"`,
      );
    }

    return updatedSvg;
  }

  // Modify <path> tags to include stroke and fill dynamically only if no gradients
  private applyPathColors(svg: string, color: string): string {
    return svg.replace(/<path([^>]*)>/g, (_, attributes: string) => {
      let modifiedAttributes = attributes;

      // Replace existing stroke color
      if (attributes.includes('stroke=')) {
        modifiedAttributes = modifiedAttributes.replace(/stroke="[^"]*"/, `stroke="${color}"`);
      }

      // Replace existing fill color
      if (attributes.includes('fill=')) {
        modifiedAttributes = modifiedAttributes.replace(/fill="[^"]*"/, `fill="${color}"`);
      }

      return `<path${modifiedAttributes}>`;
    });
  }
}
